import { Markdown } from "~/components/markdown";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

export interface RemediationEntry {
  id: string;
  idName: string;
  desc: string;
}

export interface Props {
  entries: RemediationEntry[];
}

export const CheckRemediation = ({ entries }: Props) => {
  const [selectedId, setSelectedId] = useState(entries?.[0]?.id || "");
  const [selectedIdName, setSelectedIdName] = useState(
    entries?.[0]?.idName || "",
  );

  useEffect(() => {
    if (!entries || entries.length === 0) return;

    const currentEntry = entries.find((entry) => entry.id === selectedId);
    if (currentEntry) {
      setSelectedIdName(currentEntry.idName);
      return;
    }

    if (selectedIdName) {
      const matchByName = entries.find(
        (entry) => entry.idName === selectedIdName,
      );
      if (matchByName) {
        setSelectedId(matchByName.id);
        setSelectedIdName(matchByName.idName);
        return;
      }
    }

    setSelectedId(entries[0].id);
    setSelectedIdName(entries[0].idName);
  }, [entries]);

  if (!entries || entries.length === 0) {
    return null;
  }

  if (entries.length === 1) {
    return <Markdown source={entries[0].desc} externalLinksInNew />;
  }

  const selectedRemediation = useMemo(
    () => entries.find((entry) => entry.id === selectedId) || entries[0],
    [entries, selectedId],
  );

  const handleChange = (event: SelectChangeEvent) => {
    const newId = event.target.value;
    const newEntry = entries.find((entry) => entry.id === newId);
    setSelectedId(newId);
    if (newEntry) {
      setSelectedIdName(newEntry.idName);
    }
  };

  return (
    <Box>
      <FormControl sx={{ my: 1, minWidth: 250 }}>
        <InputLabel id="remediation-select-label">
          Remediation Method
        </InputLabel>
        <Select
          labelId="remediation-select-label"
          id="remediation-select"
          value={selectedId}
          label="Remediation Method"
          onChange={handleChange}
          sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          {entries.map((remediation) => (
            <MenuItem key={remediation.id} value={remediation.id}>
              {remediation.idName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedRemediation ? (
        <Markdown source={selectedRemediation.desc} externalLinksInNew />
      ) : null}
    </Box>
  );
};
